import {EventEmitter, Injectable, isDevMode} from '@angular/core';
import {EddlRemoveFromCartEvent} from '@app/services/analytics/EDDL/events/eddl-remove-from-cart-event';
import {ArrayService, DcpGlobalMessageService, JsonService} from '@dcp/configurator';
import {UntilDestroy, untilDestroyed} from '@ngneat/until-destroy';
import {ActiveCartFacade} from '@spartacus/cart/base/root';
import {OrderEntry} from '@spartacus/cart/base/root/models/cart.model';
import {EventService} from '@spartacus/core';
import {isEmpty} from 'lodash-es';
import {take} from 'rxjs/operators';

/**
 * When cart items are deleted in the backend they disappear from the cart in the frontend.
 * Because we need to display a success message when a cart item is deleted, we need to store the position the item was.
 * This service manages the changes that are done
 */
@UntilDestroy()
@Injectable({
  providedIn: 'root',
})
export class DcpCartHistoryService {

  public innerOrderEntries: OrderEntry[] = [];
  private unchangedOrderEntries: OrderEntry[] = [];
  public orderEntriesChanged: EventEmitter<string> = new EventEmitter<string>();

  constructor(
    private activeCartFacade: ActiveCartFacade,
    private jsonService: JsonService,
    private eventService: EventService,
    private dcpGlobalMessageService: DcpGlobalMessageService
  ) {
  }

  set orderEntries(orderEntries: OrderEntry[]) {
    this.unchangedOrderEntries = orderEntries;
    this.innerOrderEntries = this.updateOrderEntries(orderEntries, this.innerOrderEntries);
    this.innerOrderEntries = this.addNewOrderEntries(orderEntries, this.innerOrderEntries);
    this.innerOrderEntries = this.markRemovedOrderEntries(orderEntries, this.innerOrderEntries);
    this.orderEntriesChanged.emit('orderEntriesSet');
  }

  /**
   * Removes an cartEntry in the backend and flags it as 'removing' and later as 'removed' in the frontend.
   * Use as entry point
   *
   * @param cartEntry to remove, it must be a cartEntry from this.innerOrderEntries
   */
  removeEntry(cartEntry: OrderEntry): void {
    this.flagAsRemoving(cartEntry);
    // This will return a 400 error if cartEntry has attributes unknown to hybris
    const orderEntryId = this.getOrderEntryId(cartEntry);
    this.activeCartFacade.removeEntry(
      this.unchangedOrderEntries.find(soe => orderEntryId === this.getOrderEntryId(soe))
    );
    this.dcpGlobalMessageService.getActiveMessages().pipe(take(1), untilDestroyed(this)).subscribe(activeMessages => {
      activeMessages
        .filter(msgObj => msgObj?.group?.startsWith('potential'))
        .forEach(msg => this.dcpGlobalMessageService.removeMessage(msg.id));
    });
    this.triggerEddlRemoveFromCartEvent(cartEntry);
  }

  /**
   * Triggers the eddl-remove-from-cart event.
   *
   * @param cartEntry to remove, it supposed to be set to the ddl-remove-from-cart event in case of legacy product.
   * In case of cps - set up the parent product code (cartEntry.product.code)
   */
  triggerEddlRemoveFromCartEvent(cartEntry: OrderEntry): void {
    const event = new EddlRemoveFromCartEvent();
    if (cartEntry.product && Object.keys(cartEntry.product)?.includes('configuratorType')) {
      event.productCode = cartEntry.product.code;
    } else {
      event.entry = cartEntry;
    }
    setTimeout(() => this.eventService.dispatch(event));
  }

  /**
   * Adds the flag removing to a cartEntry in the services list of cartEntries.
   *
   * @param cartEntry to flag as removed
   */
  flagAsRemoving(cartEntry: OrderEntry): void {
    if (isDevMode() && !this.innerOrderEntries) {
      throw new Error('orderEntries$ needs to be set before an item can be removed.');
    } else {
      // cartEntry is already part of the inner order entries array
      cartEntry.removed = 'removing';
    }
  }

  flagAsRemoved(cartEntry: OrderEntry): void {
    const innerEntry: OrderEntry = this.findOrderEntryById(this.innerOrderEntries, this.getOrderEntryId(cartEntry));
    // This check is needed because when the cart is turned into an order it looks like all entries where removed
    if (innerEntry.removed === 'removing') {
      innerEntry.removed = 'removed';
    }
  }

  /**
   * Adds order entries to innerOrderEntries no matter if its filled already or empty.
   *
   * @param subscribedOrderEntries OrderEntries array from the backend. Contains entries to add.
   * @param orderEntries OrderEntries array where entries are marked as removed.
   */
  addNewOrderEntries(subscribedOrderEntries: OrderEntry[], orderEntries: OrderEntry[]): OrderEntry[] {
    const entryIdsToAdd: string[] = ArrayService.calcDifference(
      this.getOrderEntriesIdArray(subscribedOrderEntries),
      this.getOrderEntriesIdArray(orderEntries)
    );
    for (const entryId of entryIdsToAdd) {
      orderEntries.push(
        this.jsonService.parseSafely(JSON.stringify(this.findOrderEntryById(subscribedOrderEntries, entryId))) as OrderEntry
      );
    }
    return orderEntries;
  }

  /**
   * Deletes order entries or marks them as removed to innerOrderEntries no matter if its filled already or empty.
   *
   * @param subscribedOrderEntries OrderEntries array from the backend. Misses entries that should be marked as removed.
   * @param orderEntries OrderEntries array where entries are marked as removed.
   */
  markRemovedOrderEntries(subscribedOrderEntries: OrderEntry[], orderEntries: OrderEntry[]): OrderEntry[] {
    const entryIdsToRemove: string[] = ArrayService.calcDifference(
      this.getOrderEntriesIdArray(orderEntries),
      this.getOrderEntriesIdArray(subscribedOrderEntries)
    );
    orderEntries = this.deleteRemovedEntries(orderEntries);
    for (const entryId of entryIdsToRemove) {
      this.flagAsRemoved(this.findOrderEntryById(orderEntries, entryId));
    }
    return orderEntries;
  }

  getOrderEntriesIdArray(entries: OrderEntry[]): string[] {
    if (entries?.length) {
      return entries.map(e => this.getOrderEntryId(e));
    }
    return [];
  }

  /**
   * Returns a unique id of the order entry passed into it.
   * At the moment the product id is used because OrderEntry doesn't have a code field
   * and there must be one oe per product.
   *
   * @param entry OrderEntry to get the id for
   */
  getOrderEntryId(entry: OrderEntry): string {
    return entry?.product?.code;
  }

  findOrderEntryById(orderEntries: OrderEntry[], orderEntryId: string): OrderEntry {
    return orderEntries.find(ce => (this.getOrderEntryId(ce) === orderEntryId)) || {};
  }

  /**
   * Deletes all cartEntries that are marked as removed from the array and returns it
   *
   * @param orderEntries array to delete the entries from
   */
  deleteRemovedEntries(orderEntries: OrderEntry[]): OrderEntry[] {
    return ArrayService.filterInPlace(orderEntries, e => e.removed !== 'removed');
  }

  /**
   * Replaces existing orderEntries to update the quantity or start date
   */
  updateOrderEntries(orderEntries: OrderEntry[], innerOrderEntries: OrderEntry[]) {

    for (let i: number = 0; i < innerOrderEntries.length; i++) {
      const innerEntry: OrderEntry = this.findOrderEntryById(orderEntries, this.getOrderEntryId(innerOrderEntries[i]));
      if (!isEmpty(innerEntry) && !innerOrderEntries[i].removed) {
        innerOrderEntries[i] = this.jsonService.parseSafely(JSON.stringify(innerEntry)) as OrderEntry;
      }
    }
    return innerOrderEntries;
  }
}
